import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, type MergeRow } from '../lib/api';
import { formatDate } from '../lib/format';

/**
 * Merge history — the reversibility half of deliverable 11.
 *
 * §C.3 requires that every merge undoes and restores both records, so the undo
 * has to be reachable by the person who has to use it. Two kinds of row appear:
 *
 *  - `reviewed` — performed here by a named person, and revertible here
 *  - `workbook_prefold` — one of the 20 duplicates eliminated before the load.
 *    The losing row was never in the database, so there is nothing in-app to
 *    restore; the row keeps a complete snapshot of the original record instead,
 *    and that is stated rather than papered over with a button that fails.
 */
export function MergeHistory(): JSX.Element {
  const [pageNumber, setPageNumber] = useState(1);
  const [rows, setRows] = useState<MergeRow[]>([]);
  const [meta, setMeta] = useState<{ total: number; pageSize: number; hasMore: boolean } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const page = await api.merges(pageNumber);
      setRows(page.merges);
      setMeta({ total: page.total, pageSize: page.pageSize, hasMore: page.hasMore });
      setNotice(null);
    } catch {
      setRows([]);
      setNotice('The merge history could not be loaded.');
    }
  }, [pageNumber]);

  useEffect(() => {
    void load();
  }, [load]);

  async function revert(row: MergeRow): Promise<void> {
    const reason = window.prompt('Why is this merge being undone? The reason is recorded against your name.');
    if (!reason || reason.trim().length < 3) {
      setNotice('An undo is recorded with a reason or not at all.');
      return;
    }
    setBusyId(row.id);
    try {
      await api.revertMerge(row.id, reason.trim());
      await load();
    } catch (failure) {
      setNotice(
        failure instanceof ApiFailure && failure.body.error === 'not_revertible_here'
          ? 'This is a pre-load consolidation. The losing row was never loaded, so it is restored by ' +
            're-importing from its snapshot rather than by an in-app undo.'
          : 'That undo was refused.',
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section aria-labelledby="merges-heading">
      <h1 id="merges-heading" className="mb-1 text-h1 font-semibold text-text-primary">
        Merge history
      </h1>
      <p className="mb-4 text-body text-text-secondary">
        {meta ? `${meta.total} recorded consolidation${meta.total === 1 ? '' : 's'}` : 'Loading…'} · every
        merge keeps a full snapshot of both records
      </p>

      {notice ? (
        <p role="status" className="mb-4 text-body text-warning">
          {notice}
        </p>
      ) : null}

      <ul className="grid gap-2 lg:hidden">
        {rows.map((row) => (
          <li key={row.id} className="card rounded-card border border-border bg-white p-4">
            <p className="micro-label text-text-secondary">{row.origin.replace(/_/g, ' ')}</p>
            <p className="mt-1 text-h3 font-semibold text-text-primary">{row.winner_name}</p>
            <p className="text-body text-text-secondary">
              kept · {row.loser_name ?? row.loser_legacy_jnid} folded in
            </p>
            <p className="mt-1 text-body text-text-secondary">{row.reason}</p>
          </li>
        ))}
      </ul>

      {rows.length > 0 ? (
        <div className="hidden overflow-hidden rounded-card border border-border lg:block">
          <table className="w-full border-collapse text-body">
            <caption className="sr-only">Recorded contact merges</caption>
            <thead>
              <tr className="table-header bg-navy text-white">
                <th scope="col" className="px-3 py-2 text-left text-micro font-medium uppercase">Kept</th>
                <th scope="col" className="px-3 py-2 text-left text-micro font-medium uppercase">Folded in</th>
                <th scope="col" className="px-3 py-2 text-left text-micro font-medium uppercase">Origin</th>
                <th scope="col" className="px-3 py-2 text-left text-micro font-medium uppercase">By</th>
                <th scope="col" className="px-3 py-2 text-left text-micro font-medium uppercase">Reason</th>
                <th scope="col" className="px-3 py-2 text-right text-micro font-medium uppercase">When</th>
                <th scope="col" className="px-3 py-2 text-right text-micro font-medium uppercase">Undo</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="table-row border-t border-border">
                  <td className="px-3 py-2 font-medium">{row.winner_name}</td>
                  <td className="px-3 py-2 text-text-secondary">
                    {row.loser_name ?? '—'}{' '}
                    <span className="numeric text-numeric">({row.loser_legacy_jnid})</span>
                  </td>
                  <td className="px-3 py-2">{row.origin.replace(/_/g, ' ')}</td>
                  <td className="px-3 py-2 text-text-secondary">{row.performed_by_name ?? 'import'}</td>
                  <td className="px-3 py-2 text-text-secondary">{row.reason}</td>
                  <td className="numeric px-3 py-2 text-right text-text-secondary">
                    {formatDate(row.performed_at)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {row.reverted_at ? (
                      <span className="micro-label text-text-secondary">reverted</span>
                    ) : row.origin === 'reviewed' ? (
                      <button
                        type="button"
                        disabled={busyId === row.id}
                        onClick={() => void revert(row)}
                        className="btn-destructive rounded-card px-3 text-body font-medium disabled:opacity-40"
                      >
                        Undo
                      </button>
                    ) : (
                      <span className="micro-label text-text-secondary">snapshot only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {meta && (meta.hasMore || pageNumber > 1) ? (
        <nav aria-label="Merge history pagination" className="mt-4 flex items-center justify-between gap-3">
          <p className="numeric text-numeric text-text-secondary">
            Page {pageNumber} · {meta.pageSize} rows per page
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={pageNumber <= 1}
              onClick={() => setPageNumber(pageNumber - 1)}
              className="btn-secondary rounded-card px-4 text-body font-medium disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!meta.hasMore}
              onClick={() => setPageNumber(pageNumber + 1)}
              className="btn-secondary rounded-card px-4 text-body font-medium disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </nav>
      ) : null}
    </section>
  );
}
